import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { IAdminTransaction, TransactionSortField, TransactionSortOrder } from '@/types/admin/transactions';
import { 
  getPaymentStatusColor, 
  getTransactionAmountColor,
  getPayerName,
  getTransactionTypeLabel,
  isPayment,
  isCharge
} from '@/utils/admin/transactions';

type TransactionFilterType = 'all' | 'charges' | 'payments';

interface TransactionTableProps {
  transactions: IAdminTransaction[];
  maxRows?: number;
  showPager?: boolean;
  showFilter?: boolean;
}

export const TransactionTable: React.FC<TransactionTableProps> = ({ 
  transactions, 
  maxRows = 10,
  showPager = false,
  showFilter = true,
}) => {
  const [sortField, setSortField] = useState<TransactionSortField>('date');
  const [sortOrder, setSortOrder] = useState<TransactionSortOrder>('desc');
  const [filterType, setFilterType] = useState<TransactionFilterType>('all');

  // Filter and sort transactions
  const filteredAndSortedTransactions = useMemo(() => {
    // First filter based on type
    let filtered = transactions;
    if (filterType === 'charges') {
      filtered = transactions.filter(isCharge);
    } else if (filterType === 'payments') {
      filtered = transactions.filter(isPayment);
    }

    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'date':
          comparison = new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime();
          break;
        case 'amount':
          comparison = Math.abs(a.amount) - Math.abs(b.amount);
          break;
        case 'description':
          comparison = (a.description || '').localeCompare(b.description || '');
          break;
        case 'type':
          comparison = (a.type || '').localeCompare(b.type || '');
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return maxRows ? sorted.slice(0, maxRows) : sorted;
  }, [transactions, sortField, sortOrder, maxRows, filterType]);

  const handleSort = (field: TransactionSortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIndicator = ({ field }: { field: TransactionSortField }) => {
    if (sortField !== field) return null;
    return (
      <Text style={styles.sortIndicator}>
        {sortOrder === 'asc' ? ' ↑' : ' ↓'}
      </Text>
    );
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;
    
    const color = getPaymentStatusColor(status);

    return (
      <View style={[styles.statusBadge, { backgroundColor: color + '20' }]}>
        <Text style={[styles.statusText, { color }]}>
          {status}
        </Text>
      </View>
    );
  };

  const getTypeBadge = (transaction: IAdminTransaction) => {
    const label = getTransactionTypeLabel(transaction);
    const bgColor = isPayment(transaction) 
      ? ADMIN_COLORS.TRANSACTION.PAYMENT + '15' 
      : ADMIN_COLORS.TRANSACTION.CHARGE + '15';
    const textColor = isPayment(transaction) 
      ? ADMIN_COLORS.TRANSACTION.PAYMENT 
      : ADMIN_COLORS.TRANSACTION.CHARGE;

    return (
      <View style={[styles.typeBadge, { backgroundColor: bgColor }]}>
        <Text style={[styles.typeText, { color: textColor }]}>
          {label}
        </Text>
      </View>
    );
  };

  const FilterButton = ({ type, label }: { type: TransactionFilterType; label: string }) => {
    const isActive = filterType === type;
    return (
      <TouchableOpacity
        style={[styles.filterButton, isActive && styles.filterButtonActive]}
        onPress={() => setFilterType(type)}
      >
        <Text style={[styles.filterButtonText, isActive && styles.filterButtonTextActive]}>
          {label}
        </Text>
      </TouchableOpacity>
    );
  };

  const renderRow = ({ item }: { item: IAdminTransaction }) => (
    <View style={styles.tableRow}>
      <View style={[styles.cell, styles.dateCell]}>
        <Text style={styles.cellText}>
          {formatShortDate(item.transactionDate)}
        </Text>
      </View>
      <View style={[styles.cell, styles.typeCell]}>
        {getTypeBadge(item)}
      </View>
      <View style={[styles.cell, styles.descriptionCell]}>
        <Text style={styles.cellText} numberOfLines={2}>
          {item.description}
          {item.arCodeDescription && (
            <Text style={styles.subText}>{'\n'}{item.arCodeDescription}</Text>
          )}
        </Text>
      </View>
      <View style={[styles.cell, styles.amountCell]}>
        <Text style={[
          styles.cellText,
          styles.cellTextRight,
          { color: getTransactionAmountColor(item), fontWeight: '600' }
        ]}>
          {formatCurrency(Math.abs(item.amount))}
        </Text>
      </View>
      <View style={[styles.cell, styles.statusCell]}>
        {getStatusBadge(item.paymentSettlementStatus)}
      </View>
      <View style={[styles.cell, styles.payerCell]}>
        <Text style={styles.cellText} numberOfLines={1}>
          {getPayerName(item)}
        </Text>
      </View>
      <View style={[styles.cell, styles.feeCell]}>
        <Text style={[styles.cellText, styles.cellTextRight]}>
          {item.lateFees?.amount ? formatCurrency(item.lateFees.amount) : '-'}
        </Text>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {showFilter && (
        <View style={styles.filterContainer}>
          <FilterButton type="all" label="All Transactions" />
          <FilterButton type="charges" label="Charges Only" />
          <FilterButton type="payments" label="Payments Only" />
        </View>
      )}

      <View style={styles.tableHeader}>
        <TouchableOpacity 
          style={[styles.headerCell, styles.dateCell]} 
          onPress={() => handleSort('date')}
        >
          <Text style={styles.headerText}>
            DATE
            <SortIndicator field="date" />
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.headerCell, styles.typeCell]}
          onPress={() => handleSort('type')}
        >
          <Text style={styles.headerText}>
            TYPE
            <SortIndicator field="type" />
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.headerCell, styles.descriptionCell]}
          onPress={() => handleSort('description')}
        >
          <Text style={styles.headerText}>
            DESCRIPTION
            <SortIndicator field="description" />
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.headerCell, styles.amountCell]}
          onPress={() => handleSort('amount')}
        >
          <Text style={[styles.headerText, styles.headerTextRight]}>
            AMOUNT
            <SortIndicator field="amount" />
          </Text>
        </TouchableOpacity>
        <View style={[styles.headerCell, styles.statusCell]}>
          <Text style={styles.headerText}>STATUS</Text>
        </View>
        <View style={[styles.headerCell, styles.payerCell]}>
          <Text style={styles.headerText}>PAYER</Text>
        </View>
        <View style={[styles.headerCell, styles.feeCell]}>
          <Text style={[styles.headerText, styles.headerTextRight]}>LATE FEE</Text>
        </View>
      </View>

      <FlatList
        data={filteredAndSortedTransactions}
        renderItem={renderRow}
        keyExtractor={(item) => String(item.transactionId)}
        ItemSeparatorComponent={() => <View style={styles.separator} />}
        showsVerticalScrollIndicator={false}
        style={styles.tableBody}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  filterButtonActive: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '15',
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  filterButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  filterButtonTextActive: {
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '600',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderBottomWidth: 2,
    borderBottomColor: ADMIN_COLORS.BORDER,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  headerCell: {
    paddingHorizontal: 8,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerText: {
    fontSize: 11,
    fontWeight: '700',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  headerTextRight: {
    textAlign: 'right',
    width: '100%',
  },
  sortIndicator: {
    fontSize: 10,
    color: ADMIN_COLORS.PRIMARY,
  },
  tableBody: {
    flex: 1,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
  },
  cell: {
    paddingHorizontal: 8,
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  cellTextRight: {
    textAlign: 'right',
  },
  dateCell: {
    width: 100,
  },
  typeCell: {
    width: 90,
    alignItems: 'flex-start',
  },
  descriptionCell: {
    flex: 2,
    minWidth: 80,
  },
  amountCell: {
    width: 100,
  },
  statusCell: {
    width: 160,
    alignItems: 'flex-start',
  },
  payerCell: {
    width: 120,
  },
  feeCell: {
    width: 90,
    alignItems: 'flex-end',
  },
  subText: {
    fontSize: 11,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  statusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 3,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
  },
  typeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  typeText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  separator: {
    height: 1,
    backgroundColor: ADMIN_COLORS.BORDER,
    marginHorizontal: 8,
  },
});

